
import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useRole } from "@/hooks/useRole";
import { toast } from "sonner";

type MaintenanceContextType = {
  isMaintenanceMode: boolean;
  toggleMaintenanceMode: (value: boolean) => Promise<void>;
  isLoading: boolean;
  maintenanceMessage: string;
  setMaintenanceMessage: (message: string) => Promise<void>;
};

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

export const MaintenanceProvider = ({ children }: { children: React.ReactNode }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [maintenanceMessage, setMaintenanceMessageState] = useState(
    "We're currently performing maintenance. We'll be back soon!"
  );
  
  // Get user ID from auth directly
  const getUserId = () => {
    const session = supabase.auth.getSession();
    return session.then(res => res.data.session?.user?.id || null);
  };
  
  // Get user ID for initial check
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserId = async () => {
      const id = await getUserId();
      setUserId(id);
    };
    fetchUserId();
  }, []);
  
  const { role } = useRole(userId);
  
  useEffect(() => {
    const fetchMaintenanceStatus = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('site_settings')
          .select('*')
          .eq('key', 'maintenance_mode')
          .single();
        
        if (error) {
          // If the error is not a "no rows returned" error, log it
          if (error.code !== 'PGRST116') {
            console.error('Error fetching maintenance status:', error);
          }
          // Default to no maintenance mode if there's an error
          setIsMaintenanceMode(false);
          setIsLoading(false);
          return;
        }
        
        if (data) {
          const value = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
          setIsMaintenanceMode(value?.enabled === true);
          if (value?.message) {
            setMaintenanceMessageState(value.message);
          }
        }
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
        // Default to non-maintenance mode on error
        setIsMaintenanceMode(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaintenanceStatus();
  }, []);

  const toggleMaintenanceMode = async (value: boolean) => {
    try {
      if (role !== 'admin') {
        throw new Error('Only admins can toggle maintenance mode');
      }

      const userId = await getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('site_settings')
        .upsert(
          { 
            key: 'maintenance_mode',
            value: JSON.stringify({ 
              enabled: value,
              message: maintenanceMessage,
              updated_at: new Date().toISOString(),
              updated_by: userId
            })
          },
          { onConflict: 'key' }
        );

      if (error) {
        console.error('Error updating maintenance mode:', error);
        toast.error('Failed to update maintenance mode');
        throw error;
      }
      
      setIsMaintenanceMode(value);
      toast.success(value ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
    } catch (error) {
      console.error('Error updating maintenance mode:', error);
      toast.error('Failed to update maintenance mode');
      throw error;
    }
  };

  const setMaintenanceMessage = async (message: string) => {
    try {
      if (role !== 'admin') {
        throw new Error('Only admins can update maintenance message');
      }

      const userId = await getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('site_settings')
        .upsert(
          { 
            key: 'maintenance_mode',
            value: JSON.stringify({ 
              enabled: isMaintenanceMode,
              message: message,
              updated_at: new Date().toISOString(),
              updated_by: userId
            })
          },
          { onConflict: 'key' }
        );

      if (error) {
        console.error('Error updating maintenance message:', error);
        toast.error('Failed to update maintenance message');
        throw error;
      }
      
      setMaintenanceMessageState(message);
      toast.success('Maintenance message updated');
    } catch (error) {
      console.error('Error updating maintenance message:', error);
      toast.error('Failed to update maintenance message');
      throw error;
    }
  };

  return (
    <MaintenanceContext.Provider 
      value={{ 
        isMaintenanceMode, 
        toggleMaintenanceMode, 
        isLoading,
        maintenanceMessage,
        setMaintenanceMessage
      }}
    >
      {children}
    </MaintenanceContext.Provider>
  );
};

export const useMaintenanceMode = () => {
  const context = useContext(MaintenanceContext);
  if (context === undefined) {
    throw new Error('useMaintenanceMode must be used within a MaintenanceProvider');
  }
  return context;
};
